<template>
  <div class="preika-auth-page">
    <div class="preika-auth-shell">
      <section class="preika-auth-brand">
        <img
          src="/img/avatars/preika.png"
          :alt="productName"
          class="preika-auth-brand__logo"
        >

        <div class="preika-auth-brand__content">
          <p class="preika-auth-brand__eyebrow">SECURE PASSWORD</p>
          <h1>新しいパスワードを<br>設定してください。</h1>
          <p>
            12文字以上64文字以内のパスワードを設定します。
            他サービスで使用しているパスワードの使い回しは避けてください。
          </p>
        </div>
      </section>

      <section class="preika-auth-form">
        <p class="preika-auth-form__eyebrow">RESET PASSWORD</p>
        <h2>新しいパスワード</h2>
        <p class="preika-auth-form__description">
          確認のため、同じパスワードを2回入力してください。
        </p>

        <ValidationObserver ref="obs">
          <CForm @submit.prevent="changePassword">
            <div
              v-if="errors.length"
              class="alert alert-danger"
              role="alert"
            >
              <div v-for="error in errors" :key="error">
                {{ error }}
              </div>
            </div>

            <ValidationProvider
              name="新しいパスワード"
              rules="required|password"
              v-slot="providerProps"
            >
              <label for="preika-new-password">
                新しいパスワード
                <span class="asterisk">*</span>
              </label>
              <CInput
                id="preika-new-password"
                :type="passwordInputType"
                v-model="newPassword"
                autocomplete="new-password"
                description="12文字以上64文字以内で入力してください。"
                :is-valid="providerProps.errors.length ? false : null"
              >
                <template #prepend-content>
                  <CIcon name="cil-lock-locked"/>
                </template>
              </CInput>
              <p v-if="providerProps.errors[0]" class="error">
                {{ providerProps.errors[0] }}
              </p>
            </ValidationProvider>

            <ValidationProvider
              name="新しいパスワード(確認)"
              rules="required|password|check:新しいパスワード"
              v-slot="providerProps"
            >
              <label for="preika-confirm-password">
                新しいパスワード（確認）
                <span class="asterisk">*</span>
              </label>
              <CInput
                id="preika-confirm-password"
                :type="passwordInputType"
                v-model="confirmPassword"
                autocomplete="new-password"
                :is-valid="providerProps.errors.length ? false : null"
              >
                <template #prepend-content>
                  <CIcon name="cil-lock-locked"/>
                </template>
              </CInput>
              <p v-if="providerProps.errors[0]" class="error">
                {{ providerProps.errors[0] }}
              </p>
            </ValidationProvider>

            <label class="preika-password-visibility">
              <input type="checkbox" v-model="showPassword">
              <span>入力したパスワードを表示</span>
            </label>

            <div class="preika-auth-form__actions preika-auth-form__actions--end">
              <!--
                無効にするのは送信中だけ。入力の正しさは押されてから確かめる。

                以前は observerProps.validated も条件に入れていた。これは
                「検証を実行したか」であって「入力が正しいか」ではない。
                パスワードマネージャやブラウザの自動入力は input を出さない
                ので検証が走らず、**埋まって見えるのにボタンが押せない。**
                しかも invalid ではないから、赤字も1つも出ない。

                この流れは**リンクや申し込みに期限がある。**悩んでいるうちに
                切れると、最初からやり直しになる。

                Login.vue で同じ穴を直したときと同じ形に揃えてある。
              -->
              <save-button
                label="パスワードを変更"
                loading-label="変更中"
                :loading="loading"
                :disabled="loading"
                @click="changePassword"
              />
            </div>
          </CForm>
        </ValidationObserver>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ConstList from './../../ConstList'
import { requestFailureMessage } from '@/utils/requestFailure'
import SaveButton from '../../common/SaveButton'

import { PRODUCT_NAME } from '@/common/productName'

/*
 * 通信が届かなかったとき（見直しの指摘、2026-09-30）。入力欄はそのまま
 * 残るので、もう一度押せばよいと言う。
 */
const RESET_COMMUNICATION_FAILED =
  '通信できませんでした。接続を確認してから、もう一度［パスワードを変更］を押してください。'

export default {
  name: 'ResetPassword',


  components: {
    SaveButton
  },

  props: {
    id: {
      type: String,
      default: ''
    },
    token: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      loading: false,
      errors: []
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },
    passwordInputType () {
      return this.showPassword ? 'text' : 'password'
    }
  },

  methods: {
    /**
     * 自動入力された値を、こちらの持ち物へ写す。
     *
     * パスワードマネージャは input を出さずに欄を埋めることがある。
     * そのままだと v-model が空のまま送ってしまう。
     */
    syncAutofilledValues () {
      const newPasswordField = document.getElementById('preika-new-password')
      const confirmPasswordField = document.getElementById('preika-confirm-password')

      if (newPasswordField && typeof newPasswordField.value === 'string' && newPasswordField.value !== this.newPassword) {
        this.newPassword = newPasswordField.value
      }
      if (confirmPasswordField && typeof confirmPasswordField.value === 'string' && confirmPasswordField.value !== this.confirmPassword) {
        this.confirmPassword = confirmPasswordField.value
      }
    },
    async changePassword () {
      if (this.loading) return

      this.syncAutofilledValues()

      /*
       * ボタンを常に押せるようにした分、入力の確認はここで行う。
       * 自動入力で検証が走っていない場合も、押した時点で確かめられる。
       */
      const observer = this.$refs.obs
      if (observer) {
        // 入力の確認を待つあいだに押し直されても2本目を走らせないよう、先に処理中にする
        this.loading = true
        let valid = false
        try {
          valid = await observer.validate()
        } finally {
          if (!valid) this.loading = false
        }
        if (!valid) return
      }

      this.loading = true
      this.errors = []

      axios
        .post(process.env.VUE_APP_API_ORIGIN + '/password/reset', {
          id: this.id,
          token: this.token,
          password: this.newPassword
        })
        .then(response => {
          if (response.data === ConstList.SUCCESS) {
            this.$router.push({ name: 'ResetComplete' })
            return
          }

          this.errors.push('パスワードの再設定に失敗しました。時間をおいて再度お試しください。')
        })
        .catch(async error => {
          this.reportFrontError(error)

          /*
           * **押した時点でリンクが使えなくなっていたら、開いた時点と同じ
           * 答えにする**（見直しの指摘、2026-09-30）。
           *
           * 開いたあとで15分を過ぎた・別のタブやもう一度の送信で新しい
           * メールを出した、のどれでも、次にすることは「もう一度メールを
           * 送る」。ルータが開いた時点で使えないリンクを送る先（再設定メールの
           * 画面・理由の印つき）へ、ここからも送る。
           *
           * 以前は小窓で「パスワード再設定リンクが無効または期限切れです。」と
           * 出すだけで入力欄のまま残り、何度押しても同じ小窓だった。
           */
          if (await this.linkNoLongerUsable(error)) {
            this.$router.push({
              name: 'SendEmail',
              params: { notice: 'link-unusable' }
            })
            return
          }

          /* それ以外の失敗は、画面の中に出す。小窓は閉じるまで先へ進めない。 */
          this.errors.push(requestFailureMessage(error, error && error.response
            ? ConstList.SYSTEM_ERROR
            : RESET_COMMUNICATION_FAILED))
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * 断られた理由が「リンクがもう使えない」か。
     *
     * <b>文言で見分けない。</b>400 は入力の誤りでも返る。ルータが開いた
     * 時点で使うのと同じ確かめ（/token/check）をもう一度聞き、使えないと
     * 言われたときだけ true。確かめられなければ false（画面の中に出す）。
     */
    async linkNoLongerUsable (error) {
      const status = error && error.response ? Number(error.response.status) : 0
      if (status !== 400) return false
      try {
        const response = await axios.post(
          process.env.VUE_APP_API_ORIGIN + '/token/check',
          { token: this.token })
        return response.data !== true
      } catch (checkFailed) {
        return false
      }
    },

    /** 画面の失敗を記録へ送る（これまでどおり）。結果は待たない。 */
    reportFrontError (error) {
      axios
        .post(
          process.env.VUE_APP_API_ORIGIN + '/error/frontError',
          { error: String(error) },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.preika-password-visibility {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  color: var(--preika-text-secondary);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.preika-auth-form__actions--end {
  justify-content: flex-end;
  margin-top: 24px;
}
</style>
